import { isNotDefined } from './isEmpty'

export const WHITE_HEX = '#FFFFFF'
export const BLACK_HEX = '#000000'

type Rgb = [number, number, number]

export type ColorMix = {
  from: string
  to: string
  amount: number
}

export type ContrastTarget = {
  color: string
  background: string
  minRatio: number
}

export type ColorWithOpacity = {
  color: string
  opacity: number
}

export type ReadableColorChoice = {
  background: string
  candidates: [string, ...string[]]
}

const MIX_AMOUNTS: number[] = Array.from({ length: 100 }, (_, index) => (index + 1) / 100)

export const isValidHexColor = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  return /^#[0-9A-Fa-f]{6}$/.test(value)
}

function toRgb(hex: string): Rgb {
  return [parseInt(hex.slice(1, 3), 16) / 255, parseInt(hex.slice(3, 5), 16) / 255, parseInt(hex.slice(5, 7), 16) / 255]
}

function toHexChannel(channel: number): string {
  return Math.round(Math.min(1, Math.max(0, channel)) * 255)
    .toString(16)
    .padStart(2, '0')
}

function toLinearChannel(channel: number): number {
  if (channel <= 0.04045) return channel / 12.92
  return ((channel + 0.055) / 1.055) ** 2.4
}

export function relativeLuminance(hex: string): number {
  const [red, green, blue] = toRgb(hex).map(toLinearChannel)
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export function contrastRatio(first: string, second: string): number {
  const firstLuminance = relativeLuminance(first)
  const secondLuminance = relativeLuminance(second)
  const lighter = Math.max(firstLuminance, secondLuminance)
  const darker = Math.min(firstLuminance, secondLuminance)
  return (lighter + 0.05) / (darker + 0.05)
}

export function meetsContrast({ color, background, minRatio }: ContrastTarget): boolean {
  return contrastRatio(color, background) >= minRatio
}

export function mixColors({ from, to, amount }: ColorMix): string {
  const target = toRgb(to)
  const mixed = toRgb(from).map((channel, index) => channel + (target[index] - channel) * amount)
  return `#${mixed.map(toHexChannel).join('').toUpperCase()}`
}

export function withOpacity({ color, opacity }: ColorWithOpacity): string {
  const [red, green, blue] = toRgb(color).map((channel) => Math.round(channel * 255))
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`
}

export function mostReadableColor({ background, candidates }: ReadableColorChoice): string {
  return candidates.reduce((best, candidate) => {
    if (contrastRatio(candidate, background) > contrastRatio(best, background)) return candidate
    return best
  })
}

export function ensureContrast({ color, background, minRatio }: ContrastTarget): string {
  if (meetsContrast({ color, background, minRatio })) return color
  const pole = mostReadableColor({ background, candidates: [WHITE_HEX, BLACK_HEX] })
  const amount = MIX_AMOUNTS.find((step) =>
    meetsContrast({ color: mixColors({ from: color, to: pole, amount: step }), background, minRatio })
  )
  if (isNotDefined(amount)) return pole
  return mixColors({ from: color, to: pole, amount })
}
